// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-link-wrapper
import Link from 'next/link'
import { memo } from 'react'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { NostoProductRecommendationsSection } from '../../../../brunswick/components/NostoProductRecommendationsSection'
import { Collection } from '../../../../brunswick/components/pages/Collection/Collection'
import { ErrorViewHead, ErrorViewSection } from '../../../../brunswick/components/pages/ErrorView'
import { NavContainer } from '../../../../brunswick/containers/NavContainer'
import { SeoMeta } from '../../../../components/SeoMeta'
import { canonicalBaseUrls, imageSrcs, relativePaths } from '../../../../constants/figs'
import { HeapAttributesProvider } from '../../../../containers/HeapAttributesContainer'
import { trackEvent } from '../../../../lib/analytics'
import { LoadingOverlay } from '../../../chrome/LoadingOverlay'
import { SearchOverlayForm } from '../Search/SearchOverlayForm'

import { SEARCH_REGEX } from './SearchPage'
import {
  AutoCompleteOverlay,
  AutoCompleteOverlayBottom,
  AutoCompleteOverlayTop,
  Body,
  GridWrapper,
  HiddenH1,
  NoResultsText,
  RelatedSearchTerm,
  RelatedSearches,
  ResultsCount,
  SearchOverlayWrapper,
} from './styles'
import { ViewModel } from './types'

export const Template = memo<ViewModel>(
  ({
    totalItemsCount,
    analyticsContext,
    autoCompleteOverlayVisible,
    colorsByName,
    filters,
    hasMoreItems,
    visualFiltersSection,
    visualFiltersSectionV2,
    hideAutoCompleteOverlay,
    initialSearchText,
    isLoading,
    loadMoreItems,
    numberOfResultsText,
    onAutoComplete,
    pageTitle,
    products,
    relatedSearches,
    relatedSearchesTitle,
    searchOverlayFormRef,
    statusCode,
    submitForm,
    substituteSearchTerm,
  }) => {
    const { promoBarHeightDesktopPixels, promoBarHeightMobilePixels } = NavContainer.useContainer()
    const {
      pages: {
        pageGoneError: { image, links, subheading, heading },
      },
    } = useTranslation()

    if (statusCode === 410 || SEARCH_REGEX.exec(initialSearchText) === null) {
      return (
        <>
          <SearchOverlayWrapper>
            <SearchOverlayForm
              initialSearchText={initialSearchText}
              onAutoComplete={onAutoComplete}
              onClearFormClick={hideAutoCompleteOverlay}
              onFormSubmit={submitForm}
              ref={searchOverlayFormRef}
              resultsInFrontOfBackdrop
              shouldDisplaySearchOverlay={true}
            />
          </SearchOverlayWrapper>
          <ErrorViewHead canonicalUrl={`${canonicalBaseUrls.home}${relativePaths.page410Path}`} />
          <ErrorViewSection
            heading={heading}
            subheading={subheading}
            imageAlt={image.alt}
            imageSrc={imageSrcs.pageGoneError}
            links={[
              { ...links.shopWomen, href: relativePaths.shopWomensBestSellersPath },
              { ...links.shopMen, href: relativePaths.shopMensBestSellersPath },
            ]}
          />
        </>
      )
    }

    const renderV2VisualFilters = visualFiltersSectionV2?.id
    const renderV1VisualFilters = !renderV2VisualFilters && visualFiltersSection?.id

    return (
      <>
        <SeoMeta title={pageTitle} />

        <Body>
          <AutoCompleteOverlay show={autoCompleteOverlayVisible}>
            <AutoCompleteOverlayTop
              show={autoCompleteOverlayVisible}
              promoBarHeightDesktopPixels={promoBarHeightDesktopPixels}
              promoBarHeightMobilePixels={promoBarHeightMobilePixels}
            />
            <AutoCompleteOverlayBottom
              onClick={hideAutoCompleteOverlay}
              show={autoCompleteOverlayVisible}
            />
          </AutoCompleteOverlay>
          <SearchOverlayWrapper>
            <SearchOverlayForm
              initialSearchText={initialSearchText}
              onAutoComplete={onAutoComplete}
              onClearFormClick={hideAutoCompleteOverlay}
              onFormSubmit={submitForm}
              ref={searchOverlayFormRef}
              resultsInFrontOfBackdrop
              shouldDisplaySearchOverlay={true}
            />
          </SearchOverlayWrapper>
          <HiddenH1>{`${initialSearchText} ${numberOfResultsText} Search Results`}</HiddenH1>
          <ResultsCount role='alert' aria-live='polite'>
            {numberOfResultsText}
          </ResultsCount>
          {relatedSearches && relatedSearches.length > 0 && (
            <RelatedSearches>
              <span>{relatedSearchesTitle}: </span>
              {relatedSearches?.map((searchTerm, index) => (
                <RelatedSearchTerm
                  key={index}
                  {...trackEvent({
                    category: 'search',
                    action: 'click',
                    label: 'suggested search',
                    value: searchTerm,
                    customAttributes: {
                      'data-heap-position': `${index + 1}`,
                      'data-heap-location': 'search-page',
                    },
                  })}
                >
                  <Link href={`/search?q=${searchTerm}`}>{searchTerm}</Link>
                </RelatedSearchTerm>
              ))}
            </RelatedSearches>
          )}

          {!isLoading && products.length === 0 && (
            <>
              <NoResultsText>No results for '{initialSearchText}'</NoResultsText>
              <NostoProductRecommendationsSection
                analyticsContext={{ category: 'empty-search-results::searchpage-nosto-1' }}
                fadeIn
                nostoPlacementId='searchpage-nosto-1'
                title={substituteSearchTerm}
              />

              <NostoProductRecommendationsSection
                analyticsContext={{ category: 'empty-search-results::searchpage-nosto-2' }}
                fadeIn
                nostoPlacementId='searchpage-nosto-2'
              />
            </>
          )}

          {products.length > 0 && (
            <HeapAttributesProvider initialState={{ category: 'search' }}>
              <GridWrapper>
                <Collection.Wrapper
                  totalItemsCount={totalItemsCount ?? 0}
                  heapContextAttributes={analyticsContext}
                  colors={colorsByName}
                  filters={filters}
                >
                  {renderV2VisualFilters ? (
                    <Collection.VisualFilters {...visualFiltersSectionV2} />
                  ) : null}

                  {renderV1VisualFilters ? (
                    <Collection.VisualFilters {...visualFiltersSection} />
                  ) : null}

                  <Collection.Controls isLoading={isLoading} />

                  <Collection.Grid isLoading={isLoading}>
                    {products.map((product, index) => {
                      return (
                        <Collection.GridItem
                          key={product.id}
                          index={index}
                          item={{ ...product, collectionScope: undefined }}
                        />
                      )
                    })}
                  </Collection.Grid>

                  <Collection.Footer
                    loadMore={hasMoreItems && !isLoading ? loadMoreItems : undefined}
                  />
                </Collection.Wrapper>
              </GridWrapper>
            </HeapAttributesProvider>
          )}
        </Body>

        <LoadingOverlay isLoading={isLoading} />
      </>
    )
  }
)
